// eslint-disable-next-line
// @ts-nocheck
import { itemType } from 'metadata';

const tocMenuPrefix = 'com.aras.innovator.cui_default.toc';
const tocCommandBarSectionId = '0AEAF671F8E548C494CCEC8E13F99562';
const sortOrderStep = 128;
const sortOrderForDeleted = Infinity;
const getItemWithChanges = (itemId, state) => {
	const { tocData, changes } = state;
	const item = tocData.get(itemId);
	const itemChanges = changes[itemId];
	return { ...item, ...itemChanges };
};
const getChildrenWithChanges = (state, parentId) => {
	const { tocData, changes } = state;
	const newData = new Map([...tocData]);
	for (const [id, change] of Object.entries(changes)) {
		const oldItem = tocData.get(id);
		if (!oldItem) {
			newData.set(id, change);
			continue;
		}

		newData.set(id, { ...oldItem, ...change });
	}

	const children = [];
	newData.forEach((relshipItem) => {
		if (
			relshipItem['@odata.type'] !== '#CommandBarSectionItem' ||
			relshipItem['@action'] === 'delete'
		) {
			return;
		}

		const relatedId = relshipItem.related_id;
		const relatedItem = newData.get(relatedId);
		const isChild = relatedItem['parent_menu@aras.id'] === parentId;
		if (!isChild) {
			return;
		}

		const changedRelship = newData.get(relshipItem.id);
		children.push(changedRelship);
	});

	children.sort(
		(firstItem, secondItem) => firstItem.sort_order - secondItem.sort_order
	);

	return children;
};

const addItemChanges = (id, itemChanges, state) => {
	const existingChange = state.changes[id];
	if (existingChange) {
		state.changes[id] = {
			...existingChange,
			...itemChanges
		};
	} else {
		state.changes[id] = {
			'@action': 'edit',
			...itemChanges
		};
	}

	const currentItemChanges = state.changes[id];
	if (currentItemChanges['@action'] !== 'edit') {
		return;
	}

	const originalItem = state.tocData.get(id);
	const changes = Object.entries(currentItemChanges);
	const wasItemResetToInitialState = !changes.some(
		([propName, changedValue]) => {
			if (propName === '@action' || originalItem[propName] === changedValue) {
				return false;
			}

			return true;
		}
	);

	if (wasItemResetToInitialState) {
		delete state.changes[id];
	}
};

const createRelatedItem = (props) => {
	const commandBarId = aras.generateNewGUID();
	const commandBarName = [tocMenuPrefix];
	const itemTypeId = props.additional_data?.itemTypeId;
	if (itemTypeId) {
		const itemTypeName = itemTypeId ? itemType.getItemTypeName(itemTypeId) : '';
		commandBarName.push(itemTypeName);
	}
	commandBarName.push(commandBarId);

	const relatedItem = {
		'@action': 'add',
		id: commandBarId,
		name: commandBarName.join('_'),
		...props
	};

	return relatedItem;
};

const setSelectedItem = (value, state) => {
	const { prevToSelectedId, nextToSelectedId } = getSelectedButtonNeighbours(
		value,
		state
	);
	state.prevToSelectedId = prevToSelectedId;
	state.nextToSelectedId = nextToSelectedId;
	state.selectedId = value;
};

const getSelectedButtonNeighbours = (selectedId, state) => {
	if (!selectedId) {
		return { prevToSelectedId: '', nextToSelectedId: '' };
	}

	const selectedRelationshipItem = getItemWithChanges(selectedId, state);
	const selectedRelatedItem = getItemWithChanges(
		selectedRelationshipItem.related_id,
		state
	);
	const currentItemFolderId = selectedRelatedItem['parent_menu@aras.id'];
	const allItemsOnCurrentlevel = getChildrenWithChanges(
		state,
		currentItemFolderId
	);
	const indexOfCurrentElementInFolder = allItemsOnCurrentlevel.findIndex(
		(item) => item.id === selectedRelationshipItem.id
	);
	const prevToSelectedId =
		allItemsOnCurrentlevel[indexOfCurrentElementInFolder - 1]?.id ?? '';
	const nextToSelectedId =
		allItemsOnCurrentlevel[indexOfCurrentElementInFolder + 1]?.id ?? '';

	return { prevToSelectedId, nextToSelectedId };
};

const convertButtonToType = (relshipItem, type, state) => {
	const relatedId = relshipItem['related_id'];
	const relatedItem = getItemWithChanges(relatedId, state);
	const relatedChanges = {
		'@action': 'delete'
	};
	addItemChanges(relatedId, relatedChanges, state);

	const relatedItemCopy = {
		...relatedItem,
		'@odata.type': type,
		'@action': 'add'
	};
	const propsToClear = ['id', 'name'];

	if (type === '#CommandBarButton') {
		propsToClear.push('parent_menu@aras.id');
		propsToClear.push('parent_menu@aras.keyed_name');
	}

	propsToClear.forEach((prop) => delete relatedItemCopy[prop]);

	const newRelatedItem = createRelatedItem(relatedItemCopy);
	addItemChanges(newRelatedItem.id, newRelatedItem, state);

	addItemChanges(relshipItem.id, { related_id: newRelatedItem.id }, state);
};

const normalizeSortOrders = (treeLevelItems, state) => {
	treeLevelItems.forEach((item, index) => {
		const { id } = item;
		const newSortOrder = (index + 1) * sortOrderStep;

		addItemChanges(id, { sort_order: newSortOrder }, state);
	});
};

const getRelatedItemTypeName = (state) => {
	const selectedId = state.selectedId;
	if (!selectedId) {
		return '#CommandBarButton';
	}

	const selectedRelationshipItem = getItemWithChanges(selectedId, state);
	const selectedRelatedId = selectedRelationshipItem.related_id;
	const selectedRelatedItem = getItemWithChanges(selectedRelatedId, state);
	const isRootButtonSelected =
		selectedRelatedItem['@odata.type'] === '#CommandBarButton';
	const itemTypeName = isRootButtonSelected
		? '#CommandBarButton'
		: '#CommandBarMenuButton';

	return itemTypeName;
};

export default (setState) => {
	return {
		editItem: (id, itemChanges) => {
			setState((state) => addItemChanges(id, itemChanges, state));
		},
		deleteItem: (id) => {
			setState((state) => {
				const deletedItem = getItemWithChanges(id, state);
				const isNewItem = deletedItem['@action'] === 'add';
				const relatedId = deletedItem.related_id;
				const relatedItem = getItemWithChanges(relatedId, state);

				const deletedParentId = relatedItem['parent_menu@aras.id'];
				const indexOfDeletedItem = getChildrenWithChanges(
					state,
					deletedParentId
				).findIndex((item) => item.id === id);

				if (isNewItem) {
					delete state.changes[id];
					delete state.changes[relatedId];
					setSelectedItem('', state);
				} else {
					const relshipChanges = {
						'@action': 'delete',
						sort_order: sortOrderForDeleted
					};
					addItemChanges(id, relshipChanges, state);

					const relatedChanges = {
						'@action': 'delete'
					};
					addItemChanges(relatedId, relatedChanges, state);
				}
				const childrenOfParent = getChildrenWithChanges(state, deletedParentId);
				const isFolderDeleted =
					relatedItem['@odata.type'] === '#CommandBarMenu';
				if (isFolderDeleted) {
					const childrenOfDeletedFolder = getChildrenWithChanges(
						state,
						relatedId
					);
					childrenOfParent.splice(
						indexOfDeletedItem,
						0,
						...childrenOfDeletedFolder
					);
					normalizeSortOrders(childrenOfParent, state);

					childrenOfDeletedFolder.forEach((relship) => {
						const relatedId = relship['related_id'];
						const moveToRoot = !deletedParentId;
						if (moveToRoot) {
							const relatedItem = getItemWithChanges(relatedId, state);
							const convertToButton =
								relatedItem['@odata.type'] !== '#CommandBarMenu';
							if (convertToButton) {
								convertButtonToType(relship, '#CommandBarButton', state);
								return;
							}
						}

						const relatedChanges = {
							'parent_menu@aras.id': deletedParentId
						};
						addItemChanges(relatedId, relatedChanges, state);
					});
				} else {
					childrenOfParent.forEach((child) => {
						const { id, sort_order: sortOrder } = child;
						if (sortOrder > deletedItem['sort_order']) {
							const change = { sort_order: sortOrder - sortOrderStep };
							addItemChanges(id, change, state);
						}
					});
				}
			});
		},
		discardChanges: () => {
			setState((state) => {
				state.changes = {};
				state.selectedSortingType = state.sortingType;
				setSelectedItem('', state);
			});
		},
		addRelationshipItem: (props) => {
			const relationshipItem = {
				action: 'Add',
				'@action': 'add',
				'@odata.type': '#CommandBarSectionItem',
				id: aras.generateNewGUID(),
				source_id: tocCommandBarSectionId,
				...props
			};
			setState((state) => {
				const { id, related_id: relatedId } = relationshipItem;
				state.changes[id] = relationshipItem;
				const { selectedId } = state;
				if (!selectedId) {
					const roots = getChildrenWithChanges(state);
					relationshipItem.sort_order = roots.length * sortOrderStep;
					return;
				}

				const relatedItem = state.changes[relatedId];
				const selectedRelationshipItem = getItemWithChanges(selectedId, state);
				const { related_id: selectedRelatedId } = selectedRelationshipItem;
				const selectedRelatedItem = getItemWithChanges(
					selectedRelatedId,
					state
				);
				if (selectedRelatedItem['@odata.type'] === '#CommandBarMenu') {
					const { id: parentId } = selectedRelatedItem;
					relatedItem['parent_menu@aras.id'] = parentId;
					const children = getChildrenWithChanges(state, parentId);
					relationshipItem.sort_order = children.length * sortOrderStep;
				} else {
					const parentId = selectedRelatedItem['parent_menu@aras.id'];
					const children = getChildrenWithChanges(state, parentId);

					if (selectedRelationshipItem['@action'] === 'delete') {
						relationshipItem.sort_order = (children.length + 1) * sortOrderStep;
					} else {
						const { sort_order: selectedSortOrder } = selectedRelationshipItem;
						relationshipItem.sort_order = selectedSortOrder + sortOrderStep;
						children.forEach((relship) => {
							const { id, sort_order: sortOrder } = relship;
							if (sortOrder > selectedSortOrder) {
								const change = { sort_order: sortOrder + sortOrderStep };
								addItemChanges(id, change, state);
							}
						});
					}

					relatedItem['parent_menu@aras.id'] = parentId;
				}
			});

			return relationshipItem;
		},
		addRelatedItem: (props) => {
			const relatedItem = createRelatedItem(props);
			setState((state) => {
				const type = relatedItem['@odata.type'];
				relatedItem['@odata.type'] = type ?? getRelatedItemTypeName(state);
				state.changes[relatedItem.id] = relatedItem;
			});

			return relatedItem;
		},
		selectItem: (id) => {
			setState((state) => {
				setSelectedItem(id, state);
			});
		},
		setViewAs: (props) => {
			setState((state) => {
				const prevTocData =
					state.viewAs === null ? state.tocData : state.viewAs.prevTocData;
				const prevLanguages = state.viewAs?.prevLanguages ?? state.languages;

				state.languages = prevLanguages.filter(
					(language) => language.code === state.defaultLanguageCode
				);
				state.viewAs = {
					...state.viewAs,
					...props,
					prevTocData,
					prevLanguages
				};
			});
		},
		resetViewAs: () => {
			setState((state) => {
				if (state.viewAs !== null) {
					state.tocData = state.viewAs.prevTocData;
					state.languages = state.viewAs.prevLanguages;
					state.viewAs = null;
				}
			});
		},
		setSelectedSortingType: (selectedSortingType) => {
			setState((state) => {
				state.selectedSortingType = selectedSortingType;
			});
		},
		setSortingType: (sortingType) => {
			setState((state) => {
				state.sortingType = sortingType;
			});
		},
		setSortingTypesList: (sortingTypesList) => {
			setState((state) => {
				state.sortingTypesList = sortingTypesList;
			});
		},
		swapItemSortOrders: (firstItemId, secondItemId) => {
			setState((state) => {
				const item = getItemWithChanges(firstItemId, state);
				const relatedItemId = item.related_id;
				const relatedItem = getItemWithChanges(relatedItemId, state);
				const parentMenuId = relatedItem['parent_menu@aras.id'];
				const currentLevelItems = getChildrenWithChanges(state, parentMenuId);
				normalizeSortOrders(currentLevelItems, state);

				const firstItem = getItemWithChanges(firstItemId, state);
				const firstItemSortOrder = firstItem.sort_order;

				const secondItem = getItemWithChanges(secondItemId, state);
				const secondItemSortOrder = secondItem.sort_order;

				addItemChanges(firstItemId, { sort_order: secondItemSortOrder }, state);
				addItemChanges(secondItemId, { sort_order: firstItemSortOrder }, state);
				setSelectedItem(state.selectedId, state);
			});
		},
		dropItem: (parameters) => {
			setState((state) => {
				const { itemId, targetParentId, insertPosition } = parameters;
				const draggedItem = getItemWithChanges(itemId, state);
				const relatedId = draggedItem['related_id'];
				const draggedRelatedItem = getItemWithChanges(relatedId, state);
				const currentParentId = draggedRelatedItem['parent_menu@aras.id'];
				const currentLevelItems = getChildrenWithChanges(
					state,
					currentParentId
				);

				const currentLevelItemsWithoutDraggedItem = currentLevelItems.filter(
					(item) => item.id !== draggedItem.id
				);
				normalizeSortOrders(currentLevelItemsWithoutDraggedItem, state);

				const parentMenuRelshipItem = getItemWithChanges(targetParentId, state);
				const parentMenuId = parentMenuRelshipItem.related_id;
				const targetLevelItems = getChildrenWithChanges(state, parentMenuId);

				const targetLevelItemsWithoutDraggedItem = targetLevelItems.filter(
					(item) => item.id !== draggedItem.id
				);
				targetLevelItemsWithoutDraggedItem.splice(
					insertPosition,
					0,
					draggedItem
				);
				normalizeSortOrders(targetLevelItemsWithoutDraggedItem, state);

				addItemChanges(
					draggedRelatedItem.id,
					{ 'parent_menu@aras.id': parentMenuId },
					state
				);

				const buttonType = draggedRelatedItem['@odata.type'];
				const wasButtonMovedToRoot =
					!parentMenuId && buttonType === '#CommandBarMenuButton';
				if (wasButtonMovedToRoot) {
					convertButtonToType(draggedItem, '#CommandBarButton', state);
				}

				const wasButtonMovedFromRoot =
					parentMenuId && buttonType === '#CommandBarButton';
				if (wasButtonMovedFromRoot) {
					convertButtonToType(draggedItem, '#CommandBarMenuButton', state);
				}
			});
		}
	};
};
